import React, { useState } from 'react';
import { HeaderNav } from './components/HeaderNav';
import { Hero } from './components/Hero';
import { DirectorMessage } from './components/DirectorMessage';
import { ProgrammesSection } from './components/ProgrammesSection';
import { AdmissionsRequirements } from './components/AdmissionsRequirements';
import { FacultyAndCampuses } from './components/FacultyAndCampuses';
import { JourneyWorkflow } from './components/JourneyWorkflow';
import { Footer } from './components/Footer';
import { ApplicationForm } from './components/AdmissionFunnel/ApplicationForm';
import { ScreeningPortal } from './components/AdmissionFunnel/ScreeningPortal';
import { AdmissionLetter } from './components/AdmissionFunnel/AdmissionLetter';
import { EnrolmentPortal } from './components/AdmissionFunnel/EnrolmentPortal';
import { LMSContainer } from './components/LMS/LMSContainer';
import { CentralLoginHub } from './components/LMS/CentralLoginHub';
import { StudentAuthPage } from './components/LMS/Auth/StudentAuthPage';
import { DirectorAuthPage } from './components/LMS/Auth/DirectorAuthPage';
import { ContentManagerAuthPage } from './components/LMS/Auth/ContentManagerAuthPage';
import { FacultyAuthPage } from './components/LMS/Auth/FacultyAuthPage';
import { AdmissionAdvert } from './components/AdmissionAdvert';
import { ProgrammeDetailPage } from './components/ProgrammeDetailPage';
import { LegalPage } from './components/Legal/LegalPage';
import { AUTH_CREDENTIALS, USER_ROLES } from './data/lmsAuthData';
import { INITIAL_APPLICANTS } from './data/portalData';

export function App() {
  const [activeView, setActiveView] = useState('home');
  const [selectedStudent, setSelectedStudent] = useState('student-dba');
  const [selectedProgrammeId, setSelectedProgrammeId] = useState('dba');
  const [applicants, setApplicants] = useState(INITIAL_APPLICANTS);
  const [currentApplicantForOffer, setCurrentApplicantForOffer] = useState(null);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);

  const handleLoginSuccess = (userCredential) => {
    setAuthenticatedUser(userCredential);
    if (userCredential.role === USER_ROLES.DIRECTOR) {
      setActiveView('lms-director');
    } else if (userCredential.role === USER_ROLES.CONTENT_MANAGER) {
      setActiveView('lms-manager');
    } else if (userCredential.role === USER_ROLES.FACULTY) {
      setActiveView('lms-faculty');
    } else {
      if (userCredential.studentId) {
        setSelectedStudent(userCredential.studentId);
      }
      setActiveView('lms-student');
    }
  };

  const handleLogout = () => {
    setAuthenticatedUser(null);
    setActiveView('lms-hub');
  };

  const handleSelectProgramme = (progId) => {
    setSelectedProgrammeId(progId);
    setActiveView('programme-detail');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleApplicationSubmitted = (newApplicant) => {
    setApplicants(prev => [newApplicant, ...prev]);
    setCurrentApplicantForOffer(newApplicant);
  };

  const handleUpdateApplicantStatus = (appNo, newStatus) => {
    setApplicants(prev => prev.map(app => {
      if (app.applicationNo === appNo) {
        return {
          ...app,
          screeningStatus: newStatus,
          admissionLetterIssued: newStatus === 'Approved',
          matricNo: newStatus === 'Approved' ? (app.matricNo || `AKSU/BS/2026/EXEC/${Math.floor(100 + Math.random() * 900)}`) : app.matricNo
        };
      }
      return app;
    }));
  };

  const handleSelectApplicantForOffer = (applicant) => {
    setCurrentApplicantForOffer(applicant);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans overflow-x-hidden print:min-h-0 print:bg-white print:block">
      
      {/* Navigation Bar */}
      <HeaderNav 
        activeView={activeView} 
        setActiveView={setActiveView}
        selectedStudent={selectedStudent}
        setSelectedStudent={setSelectedStudent}
        onSelectProgramme={handleSelectProgramme}
      />

      {/* Main Content Area based on Active View */}
      <main className="flex-1 print:block print:w-full print:p-0 print:m-0">
        {activeView === 'home' && (
          <>
            {/* World Class Premium Web View of the Home */}
            <Hero setActiveView={setActiveView} />
            <DirectorMessage setActiveView={setActiveView} />
            <ProgrammesSection 
              setActiveView={setActiveView} 
              onSelectProgramme={handleSelectProgramme}
            />
            <AdmissionsRequirements setActiveView={setActiveView} />
            <FacultyAndCampuses />
            <JourneyWorkflow setActiveView={setActiveView} />
          </>
        )}

        {activeView === 'programme-detail' && (
          <ProgrammeDetailPage 
            programmeId={selectedProgrammeId}
            onSelectProgramme={setSelectedProgrammeId}
            setActiveView={setActiveView}
          />
        )}

        {activeView === 'admission-advert' && (
          <AdmissionAdvert setActiveView={setActiveView} />
        )}

        {activeView === 'apply' && (
          <ApplicationForm 
            setActiveView={setActiveView}
            onApplicationSubmitted={handleApplicationSubmitted}
          />
        )}

        {activeView === 'screening' && (
          <ScreeningPortal 
            setActiveView={setActiveView}
            applicants={applicants}
            onUpdateStatus={handleUpdateApplicantStatus}
            onSelectApplicantForOffer={handleSelectApplicantForOffer}
          />
        )}

        {activeView === 'offer-letter' && (
          <AdmissionLetter 
            applicant={currentApplicantForOffer}
            setActiveView={setActiveView}
            onAcceptancePaid={(applicant) => {
              setCurrentApplicantForOffer(applicant);
            }}
          />
        )}

        {activeView === 'enrolment' && (
          <EnrolmentPortal 
            setActiveView={setActiveView}
            setSelectedStudent={setSelectedStudent}
          />
        )}

        {activeView === 'privacy' && (
          <LegalPage initialTab="privacy" setActiveView={setActiveView} />
        )}

        {activeView === 'terms' && (
          <LegalPage initialTab="terms" setActiveView={setActiveView} />
        )}

        {activeView === 'cookies' && (
          <LegalPage initialTab="cookies" setActiveView={setActiveView} />
        )}

        {(activeView === 'lms-hub' || activeView === 'lms-login') && (
          <CentralLoginHub setActiveView={setActiveView} />
        )}

        {activeView === 'lms-login-student' && (
          <StudentAuthPage 
            onLoginSuccess={handleLoginSuccess}
            setActiveView={setActiveView}
            setSelectedStudent={setSelectedStudent}
          />
        )}

        {activeView === 'lms-login-director' && (
          <DirectorAuthPage 
            onLoginSuccess={handleLoginSuccess}
            setActiveView={setActiveView}
          />
        )}

        {activeView === 'lms-login-manager' && (
          <ContentManagerAuthPage 
            onLoginSuccess={handleLoginSuccess}
            setActiveView={setActiveView}
          />
        )}

        {activeView === 'lms-login-faculty' && (
          <FacultyAuthPage 
            onLoginSuccess={handleLoginSuccess}
            setActiveView={setActiveView}
          />
        )}

        {activeView === 'lms-director' && (
          <LMSContainer 
            initialRole="director" 
            selectedStudentId={selectedStudent} 
            onSelectStudent={setSelectedStudent}
            setActiveView={setActiveView}
            authenticatedUser={authenticatedUser}
            onLogout={handleLogout}
          />
        )}

        {activeView === 'lms-manager' && (
          <LMSContainer 
            initialRole="content-manager" 
            selectedStudentId={selectedStudent} 
            onSelectStudent={setSelectedStudent}
            setActiveView={setActiveView}
            authenticatedUser={authenticatedUser}
            onLogout={handleLogout}
          />
        )}

        {activeView === 'lms-faculty' && (
          <LMSContainer 
            initialRole="faculty" 
            selectedStudentId={selectedStudent} 
            onSelectStudent={setSelectedStudent}
            setActiveView={setActiveView}
            authenticatedUser={authenticatedUser}
            onLogout={handleLogout}
          />
        )}

        {activeView === 'lms-student' && (
          <LMSContainer 
            initialRole="student" 
            selectedStudentId={selectedStudent} 
            onSelectStudent={setSelectedStudent}
            setActiveView={setActiveView}
            authenticatedUser={authenticatedUser}
            onLogout={handleLogout}
          />
        )}
      </main>

      {/* Footer (displayed on home and general pages) */}
      {!activeView.startsWith('lms') && (
        <Footer 
          setActiveView={setActiveView} 
          onSelectProgramme={handleSelectProgramme}
        />
      )}

    </div>
  );
}

export default App;
